<template>
  <div class="admin-report">
    <h3 class="admin-report__title">{{report.title}}</h3>

    <div class="admin-report__content">
      <p v-if="missingParameters" class="admin-report__error">
        Report input parameters were not received from the API.
      </p>

      <div v-if="filters && filters.length" class="admin-report__filters">
        <report-filter
          v-for="filter in filters"
          :key="filter.label"
          :filter="filter"
          :value.sync="filter.value"
          class="admin-report__filter"
        />
      </div>

      <base-button class="admin-report__apply-button" :disabled="loading || missingParameters" @click="getReport">
        <template v-if="!loading">Apply</template>
        <clip-loader color="white" size="15px" :loading="loading" />
      </base-button>
    </div>

    <report-item v-for="report in reportData" :key="report.title" class="admin-report__content" :report="report" />
  </div>
</template>

<script>
import ReportFilter from './ReportFilter.vue'
import ReportItem from './ReportItem.vue'
import BaseButton from '@components/common/BaseButton.vue'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'

import toast from '@utils/toast'
import { mapGetters } from 'vuex'
import store from '../../../store'

export default {
  name: 'AdminReport',
  components: { ReportFilter, ReportItem, BaseButton, ClipLoader },
  props: {
    report: { type: Object, required: true }
  },

  data() {
    return {
      store,
      reportData: [],
      filters: [],
      errors: [],
      loading: false,
      missingParameters: false
    }
  },

  computed: {
    ...mapGetters({
      user: 'getUser',
      token: 'getToken',
      schema: 'getSchema'
    }),

    formattedParams() {
      return this.filters.map(filter => ({
        name: filter.label,
        value: filter.value
      }))
    }
  },

  created() {
    const parameters = this.normalizeParameters(this.report.parameters)

    this.missingParameters = parameters === null

    if (this.missingParameters) {
      return
    }

    const arrForFilters = parameters.map(param => {
      if (param.type === 'list' || param.type === 'multiselect') {
        param.values = this.itemsForSelect(param.values)
      }
      return param
    })
    this.filters = arrForFilters.map(param => ({
      ...param,
      value: param.default,
      error: ''
    }))
  },

  methods: {
    validateFilters() {
      this.errors = []
      this.filters.forEach(filter => {
        if (filter.mandatory && ((!filter.value && filter.value !== 0) || filter.value.length === 0) ) {
          this.errors.push(`${filter.label} is required`)
        }
      })

      if (this.errors.length) {
        toast.errorMessage(this.errors.join('<br/>'))
      }

      return this.errors.length === 0
    },

    getReport() {
      const valid = this.validateFilters()

      if (valid) {
        this.loading = true
        this.fetchReport()
          .finally(() => this.loading = false)
      }
    },

    fetchReport() {
      const data =  {
        API_KEY: this.store.getters.keys.api_key,
        code: this.token.token,
        user: this.user.user,
        data: {
          report_id: this.report.report_id,
          parameters: this.formattedParams
        }
      }

      return this.request('/admin/get/report/', 'POST', data)
        .then(response => {
          if (response.code === 0) {
            this.reportData = { ...response.data }
          } else {
            toast.errorMessage(response.message)
          }
        })
        .catch((error) => {
          toast.errorMessage(error)
        })
    },

    normalizeParameters(parameters) {
      if (parameters === null || parameters === undefined) {
        return null
      }

      if (Array.isArray(parameters)) {
        return parameters
      }

      if (typeof parameters === 'string') {
        try {
          const parsed = JSON.parse(parameters)
          return Array.isArray(parsed) ? parsed : []
        } catch (e) {
          return []
        }
      }

      if (typeof parameters === 'object') {
        return Object.values(parameters)
      }

      return []
    },

    itemsForSelect(arr) {
      return (arr || []).map(item => ({
        value: item.id,
        label: item.label
      }))
    },
  }
};
</script>

<style>
.admin-report__filters {
  display: flex;
  flex-wrap: wrap;
}

.admin-report__filter {
  margin: 0.5rem;
}

.admin-report__apply-button {
  margin-left: 0.5rem;
  width: 100px;
}

.admin-report__error {
  margin: 0 0 1rem 0.5rem;
  color: #c0392b;
}

.admin-report__report {
  margin-top: 2rem;
}

.admin-report__content {
  width: 100%;
  padding: 15px;
  margin-bottom: 25px;
  border-radius: 5px;
  box-shadow: 0px 0px 10px rgba(15, 32, 47, 0.5);
}
</style>
